import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, ActivityIndicator, RefreshControl, Dimensions, Share, Modal, Pressable, Alert, Linking, KeyboardAvoidingView, TextInput, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as Clipboard from 'expo-clipboard';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';
import { getTimeline, like, unlike, repost, unrepost, getDid, createPost } from '../services/bluesky';
import { extractPlainText } from '../utils/text';
import { savePost } from '../utils/saved';
import Hyperlink from '../components/Hyperlink';
import VideoEmbed from '../components/VideoEmbed';
import { Theme, useThemedStyles, useTheme } from '../theme';

const CACHE_KEY_TIMELINE = 'cache_timeline';
const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface TimelineProps {
  onSelectPost?: (post: any) => void;
  onSelectUser?: (did: string) => void;
  onSearch?: () => void;
  onSearchHashtag?: (hashtag: string) => void;
  onSearchHandle?: (handle: string) => void;
}

const Timeline: React.FC<TimelineProps> = ({ onSelectPost, onSelectUser, onSearch, onSearchHashtag, onSearchHandle }) => {
  const insets = useSafeAreaInsets();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [postMenuVisible, setPostMenuVisible] = useState(false);
  const [selectedPostForMenu, setSelectedPostForMenu] = useState<any>(null);
  const [quoteModalVisible, setQuoteModalVisible] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [isQuoteSending, setIsQuoteSending] = useState(false);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  const getCacheKey = useCallback(() => {
    const did = getDid();
    return `${CACHE_KEY_TIMELINE}_${did}`;
  }, []);

  const loadCache = useCallback(async () => {
    try {
      const key = getCacheKey();
      const cached = await AsyncStorage.getItem(key);
      if (cached) {
        setPosts(JSON.parse(cached));
        setLoading(false);
      }
    } catch (e) {
      console.error('Failed to load timeline cache', e);
    }
  }, [getCacheKey]);

  const fetchTimeline = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await getTimeline(isRefresh ? undefined : cursor);
      
      // Filter out replies (unless they are reposts)
      const filteredFeed = res.feed.filter((item: any) => !item.reply || item.reason);

      setPosts(prev => {
        const existingKeys = new Set(prev.map(item => item.post.uri + (item.reason?.indexedAt || '')));
        const newItems = [];
        for (const item of filteredFeed) {
          const key = item.post.uri + (item.reason?.indexedAt || '');
          if (!existingKeys.has(key)) {
            newItems.push(item);
            existingKeys.add(key);
          }
        }
        // Refresh prepends new posts at the top (infinite-scroll style) instead of
        // replacing the whole list, so the user's scroll position is preserved.
        const updated = isRefresh ? [...newItems, ...prev] : [...prev, ...newItems];
        const key = getCacheKey();
        AsyncStorage.setItem(key, JSON.stringify(updated));
        return updated;
      });
      
      setCursor(res.cursor);
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }, [cursor, getCacheKey]);

  useEffect(() => {
    loadCache().then(() => {
      fetchTimeline(true);
    });
  }, []);

  const handleLike = async (item: any) => {
    const { post } = item;
    
    // Guard against rapid clicks while an action is pending
    if (post.viewer?.like === 'pending') return;

    const isLiked = !!post.viewer?.like;
    const previousLikeUri = post.viewer?.like;
    const previousCount = post.likeCount || 0;

    // Optimistic UI update
    setPosts(prev => prev.map(p => {
      if (p.post.cid === post.cid || p.post.uri === post.uri) {
        return {
          ...p,
          post: {
            ...p.post,
            likeCount: Math.max(0, previousCount + (isLiked ? -1 : 1)),
            viewer: { ...p.post.viewer, like: isLiked ? undefined : 'pending' }
          }
        };
      }
      return p;
    }));

    try {
      if (isLiked) {
        await unlike(previousLikeUri);
      } else {
        const res = await like(post.uri, post.cid);
        // Update with actual URI
        setPosts(prev => prev.map(p => {
          if (p.post.cid === post.cid || p.post.uri === post.uri) {
            return {
              ...p,
              post: {
                ...p.post,
                viewer: { ...p.post.viewer, like: res.data.uri }
              }
            };
          }
          return p;
        }));
      }
    } catch (e) {
      console.error('Like failed', e);
      // Revert on error
      setPosts(prev => prev.map(p => {
        if (p.post.cid === post.cid || p.post.uri === post.uri) {
          return {
            ...p,
            post: {
              ...p.post,
              likeCount: previousCount,
              viewer: { ...p.post.viewer, like: previousLikeUri }
            }
          };
        }
        return p;
      }));
    }
  };

  const handleRepost = async (item: any) => {
    const { post } = item;
    
    // Guard against rapid clicks while an action is pending
    if (post.viewer?.repost === 'pending') return;

    const isReposted = !!post.viewer?.repost;
    const previousRepostUri = post.viewer?.repost;
    const previousCount = post.repostCount || 0;

    if (isReposted) {
      // Optimistic UI update for unrepost
      setPosts(prev => prev.map(p => {
        if (p.post.cid === post.cid || p.post.uri === post.uri) {
          return {
            ...p,
            post: {
              ...p.post,
              repostCount: Math.max(0, previousCount - 1),
              viewer: { ...p.post.viewer, repost: undefined }
            }
          };
        }
        return p;
      }));

      try {
        await unrepost(previousRepostUri);
      } catch (e) {
        console.error('Unrepost failed', e);
        // Revert on error
        setPosts(prev => prev.map(p => {
          if (p.post.cid === post.cid || p.post.uri === post.uri) {
            return {
              ...p,
              post: {
                ...p.post,
                repostCount: previousCount,
                viewer: { ...p.post.viewer, repost: previousRepostUri }
              }
            };
          }
          return p;
        }));
      }
    } else {
      setSelectedPostForMenu(item);
      setPostMenuVisible(true);
    }
  };

  const executeRepost = async () => {
    if (!selectedPostForMenu) return;
    const { post } = selectedPostForMenu;
    
    // Guard against rapid clicks while an action is pending
    if (post.viewer?.repost === 'pending') return;

    const previousCount = post.repostCount || 0;
    setPostMenuVisible(false);

    // Optimistic UI update
    setPosts(prev => prev.map(p => {
      if (p.post.cid === post.cid || p.post.uri === post.uri) {
        return {
          ...p,
          post: {
            ...p.post,
            repostCount: previousCount + 1,
            viewer: { ...p.post.viewer, repost: 'pending' }
          }
        };
      }
      return p;
    }));

    try {
      const res = await repost(post.uri, post.cid);
      // Update with actual URI
      setPosts(prev => prev.map(p => {
        if (p.post.cid === post.cid || p.post.uri === post.uri) {
          return {
            ...p,
            post: {
              ...p.post,
              viewer: { ...p.post.viewer, repost: res.data.uri }
            }
          };
        }
        return p;
      }));
    } catch (e) {
      console.error('Repost failed', e);
      // Revert on error
      setPosts(prev => prev.map(p => {
        if (p.post.cid === post.cid || p.post.uri === post.uri) {
          return {
            ...p,
            post: {
              ...p.post,
              repostCount: previousCount,
              viewer: { ...p.post.viewer, repost: undefined }
            }
          };
        }
        return p;
      }));
    }
  };

  const executeQuote = () => {
    if (!selectedPostForMenu) return;
    setPostMenuVisible(false);
    setQuoteText('');
    setQuoteModalVisible(true);
  };

  const handleSendQuote = async () => {
    if (!quoteText.trim() || !selectedPostForMenu) return;
    setIsQuoteSending(true);
    try {
      const { post } = selectedPostForMenu;
      await createPost({
        text: quoteText,
        quote: {
          uri: post.uri,
          cid: post.cid
        }
      });
      setQuoteText('');
      setQuoteModalVisible(false);
      Alert.alert('成功', '引用リポストを投稿しました');
      fetchTimeline(true);
    } catch (e) {
      console.error('Quote repost failed', e);
      Alert.alert('エラー', '引用リポストの投稿に失敗しました');
    } finally {
      setIsQuoteSending(false);
    }
  };

  const executeSave = async () => {
    if (!selectedPostForMenu) return;
    const { post } = selectedPostForMenu;
    setPostMenuVisible(false);
    await savePost(post);
    Alert.alert(t('messageBubble.saved'));
  };

  const executeCopyLink = async () => {
    if (!selectedPostForMenu) return;
    const { post } = selectedPostForMenu;
    setPostMenuVisible(false);
    const url = `https://bsky.app/profile/${post.author.handle}/post/${post.uri.split('/').pop()}`;
    await Clipboard.setStringAsync(url);
    Alert.alert(t('postDetail.copyDone'));
  };

  const executeOpenInBrowser = () => {
    if (!selectedPostForMenu) return;
    const { post } = selectedPostForMenu;
    setPostMenuVisible(false);
    const url = `https://bsky.app/profile/${post.author.handle}/post/${post.uri.split('/').pop()}`;
    Linking.openURL(url);
  };

  const handlePostMenu = (item: any) => {
    setSelectedPostForMenu(item);
    setPostMenuVisible(true);
  };

  const handleShare = async (post: any, event: any) => {
    event?.stopPropagation();
    try {
      const url = `https://bsky.app/profile/${post.author.handle}/post/${post.uri.split('/').pop()}`;
      await Share.share({
        message: url,
      });
    } catch (e) {
      console.error('Share failed', e);
    }
  };

  const formatPostTime = (indexedAt: string) => {
    const now = new Date();
    const postDate = new Date(indexedAt);
    const diffMs = now.getTime() - postDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('common.justNow') || 'たった今';
    if (diffMins < 60) return `${diffMins}${t('common.minutesAgo') || '分前'}`;
    if (diffHours < 24) return `${diffHours}${t('common.hoursAgo') || '時間前'}`;
    if (diffDays < 7) return `${diffDays}${t('common.daysAgo') || '日前'}`;
    
    return postDate.toLocaleDateString();
  };

  const renderExternal = (embed: any) => {
    const external = embed?.external || embed?.media?.external;
    if (!external) return null;

    const { uri, title, description, thumb } = external;

    return (
      <TouchableOpacity 
        style={styles.externalBox}
        onPress={() => Linking.openURL(uri)}
        activeOpacity={0.8}
      >
        {thumb && (
          <Image 
            source={{ uri: thumb }} 
            style={styles.externalThumb} 
            contentFit="cover"
          />
        )}
        <View style={styles.externalContent}>
          <Text style={styles.externalTitle} numberOfLines={2}>{title || uri}</Text>
          {description ? (
            <Text style={styles.externalDescription} numberOfLines={2}>{description}</Text>
          ) : null}
          <Text style={styles.externalUri} numberOfLines={1}>
            {uri.split('/')[2] || uri}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderItem = ({ item }: { item: any }) => {
    const post = item.post;
    const reason = item.reason;
    const isRepost = reason?.$type === 'app.bsky.feed.defs#reasonRepost';
    
    // Handle quote posts
    let quotedPost = null;
    if (post.embed?.$type === 'app.bsky.embed.record#view') {
      quotedPost = post.embed.record;
    } else if (post.embed?.$type === 'app.bsky.embed.recordWithMedia#view') {
      quotedPost = post.embed.record?.record;
    }

    const author = post.author;
    const hasImages = (post.embed?.images && post.embed.images.length > 0) || 
                      (post.embed?.media?.images && post.embed.media.images.length > 0);
    const images = post.embed?.images || post.embed?.media?.images || [];
    const imageCount = images.length;

    const videoEmbed = post.embed?.$type === 'app.bsky.embed.video#view'
      ? post.embed
      : (post.embed?.$type === 'app.bsky.embed.recordWithMedia#view' && post.embed.media?.$type === 'app.bsky.embed.video#view'
        ? post.embed.media
        : null);

    const quotedVideoEmbed = quotedPost?.embed?.$type === 'app.bsky.embed.video#view'
      ? quotedPost.embed
      : (quotedPost?.embed?.$type === 'app.bsky.embed.recordWithMedia#view' && quotedPost.embed.media?.$type === 'app.bsky.embed.video#view'
        ? quotedPost.embed.media
        : null);

    return (
      <View>
        {/* Repost Header */}
        {isRepost && (
          <View style={styles.repostHeader}>
            <Ionicons name="repeat" size={16} color={theme.colors.textMuted} style={{ marginLeft: 52 }} />
            <Text style={styles.repostText}>
              {reason.by.displayName || reason.by.handle}がリポストしました
            </Text>
          </View>
        )}

        <TouchableOpacity 
          style={styles.postItem} 
          onPress={() => onSelectPost?.(post)}
          activeOpacity={0.7}
        >
          {/* Post Header with Author Info */}
          <View style={styles.postHeader}>
            <TouchableOpacity onPress={() => onSelectUser?.(author.did)}>
              <Image 
                source={{ uri: author?.avatar || 'https://via.placeholder.com/40' }} 
                style={styles.postAvatar as any} 
              />
            </TouchableOpacity>
            <View style={styles.postAuthorInfo}>
              <View style={styles.postAuthorNameRow}>
                <TouchableOpacity onPress={() => onSelectUser?.(author.did)}>
                  <Text style={styles.postAuthorName} numberOfLines={1}>
                    {author?.displayName || author?.handle}
                  </Text>
                </TouchableOpacity>
                <Text style={styles.postTime}>· {formatPostTime(post.indexedAt)}</Text>
              </View>
              <Text style={styles.postAuthorHandle} numberOfLines={1}>@{author?.handle}</Text>
            </View>
          </View>

          {/* Post Content */}
          <View style={styles.postContent}>
            {post.record?.text && (
              <Hyperlink 
                linkDefault={true} 
                linkStyle={{ color: theme.colors.primary, textDecorationLine: 'underline' }} 
                style={styles.postText}
                onHashtagPress={onSearchHashtag}
                onHandlePress={onSearchHandle}
              >
                {post.record.text}
              </Hyperlink>
            )}
            
            {/* Images Grid */}
            {hasImages && (
              <View style={styles.postImagesGrid}>
                {images.slice(0, 4).map((img: any, i: number) => {
                  let imageStyle: any[] = [styles.postImage];
                  
                  if (imageCount === 1) {
                    imageStyle.push(styles.postImageSingle);
                  } else if (imageCount === 2) {
                    imageStyle.push(styles.postImageDouble);
                  } else if (imageCount === 3) {
                    if (i === 0) {
                      imageStyle.push(styles.postImageTripleFirst);
                    } else {
                      imageStyle.push(styles.postImageTripleOther);
                    }
                  } else {
                    imageStyle.push(styles.postImageQuad);
                  }

                  return (
                    <View key={i} style={imageStyle}>
                      <Image 
                        source={{ uri: img.thumb }} 
                        style={styles.postImageFull as any}
                        contentFit="cover"
                      />
                    </View>
                  );
                })}
              </View>
            )}

            {/* Video / GIF Embed */}
            {videoEmbed?.playlist && (
              <View style={styles.postVideoWrapper}>
                <VideoEmbed uri={videoEmbed.playlist} aspectRatio={videoEmbed.aspectRatio} />
              </View>
            )}

            {/* External Link Card */}
            {renderExternal(post.embed)}

            {/* Quoted Post */}
            {quotedPost && (
              <TouchableOpacity 
                style={styles.quotedPost}
                onPress={(e) => {
                  e.stopPropagation();
                  onSelectPost?.(quotedPost);
                }}
              >
                <View style={{ flexDirection: 'row' }}>
                  <View style={{ flex: 1 }}>
                    <View style={styles.quotedPostHeader}>
                      <Image 
                        source={{ uri: quotedPost.author?.avatar || 'https://via.placeholder.com/20' }} 
                        style={styles.quotedPostAvatar as any} 
                      />
                      <Text style={styles.quotedPostAuthor} numberOfLines={1}>
                        {quotedPost.author?.displayName || quotedPost.author?.handle}
                      </Text>
                      <Text style={styles.quotedPostHandle} numberOfLines={1}>
                        @{quotedPost.author?.handle}
                      </Text>
                    </View>
                    {quotedPost.value?.text && (
                      <Hyperlink 
                        linkDefault={true} 
                        linkStyle={{ color: theme.colors.primary, textDecorationLine: 'underline' }} 
                        style={styles.quotedPostText}
                        onHashtagPress={onSearchHashtag}
                        onHandlePress={onSearchHandle}
                        numberOfLines={3}
                      >
                        {quotedPost.value.text}
                      </Hyperlink>
                    )}
                  </View>
                  
                  {/* Quoted Post Image Preview */}
                  {(quotedPost.embed?.images && quotedPost.embed.images.length > 0) && (
                    <Image 
                      source={{ uri: quotedPost.embed.images[0].thumb }} 
                      style={styles.quotedPostImagePreview as any}
                    />
                  )}
                  {quotedPost.embed?.$type === 'app.bsky.embed.recordWithMedia#view' && 
                   quotedPost.embed.media?.images && quotedPost.embed.media.images.length > 0 && (
                    <Image 
                      source={{ uri: quotedPost.embed.media.images[0].thumb }} 
                      style={styles.quotedPostImagePreview as any}
                    />
                  )}
                  {/* Quoted Post Video Preview */}
                  {quotedVideoEmbed?.playlist && (
                    <View style={styles.quotedPostVideoPreview}>
                      <VideoEmbed uri={quotedVideoEmbed.playlist} aspectRatio={quotedVideoEmbed.aspectRatio} style={styles.quotedPostVideoPreviewInner} />
                    </View>
                  )}
                </View>
              </TouchableOpacity>
            )}
          </View>

          {/* Post Footer with Interaction Buttons */}
          <View style={styles.postFooter}>
            <View style={styles.postInteractions}>
              <TouchableOpacity 
                style={styles.postInteractionButton}
                onPress={(e) => {
                  e.stopPropagation();
                  onSelectPost?.(post);
                }}
              >
                <Ionicons name="chatbubble-outline" size={18} color={theme.colors.textMuted} />
                <Text style={styles.postInteractionText}>{post.replyCount || 0}</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.postInteractionButton}
                onPress={(e) => {
                  e.stopPropagation();
                  handleRepost(item);
                }}
              >
                <Ionicons 
                  name={post.viewer?.repost ? "repeat" : "repeat-outline"} 
                  size={20} 
                  color={post.viewer?.repost ? theme.colors.success : theme.colors.textMuted} 
                />
                <Text style={[styles.postInteractionText, post.viewer?.repost && { color: theme.colors.success }]}>
                  {post.repostCount || 0}
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.postInteractionButton}
                onPress={(e) => {
                  e.stopPropagation();
                  handleLike(item);
                }}
              >
                <Ionicons 
                  name={post.viewer?.like ? "heart" : "heart-outline"} 
                  size={18} 
                  color={post.viewer?.like ? theme.colors.danger : theme.colors.textMuted} 
                />
                <Text style={[styles.postInteractionText, post.viewer?.like && { color: theme.colors.danger }]}>
                  {post.likeCount || 0}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={styles.postInteractionButton}
                onPress={(e) => handleShare(post, e)}
              >
                <Ionicons name="share-outline" size={18} color={theme.colors.textMuted} />
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top, paddingLeft: insets.left, paddingRight: insets.right }]}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>TL</Text>
        <View style={styles.headerIcons}>
          <TouchableOpacity style={styles.headerIcon} onPress={onSearch}>
            <Ionicons name="search-outline" size={24} color={theme.colors.text} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerIcon}>
            <Ionicons name="notifications-outline" size={24} color={theme.colors.text} />
          </TouchableOpacity>
        </View>
      </View>

      {loading && posts.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      ) : (
        <FlatList
          data={posts}
          renderItem={renderItem}
          keyExtractor={(item) => item.post.uri + (item.reason?.indexedAt || '')}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={() => fetchTimeline(true)} tintColor={theme.colors.primary} colors={[theme.colors.primary]} />
          }
          maintainVisibleContentPosition={{ minIndexForVisible: 0, autoscrollToTopThreshold: 50 }}
          onEndReached={() => {
            if (!loadingMore && cursor) {
              setLoadingMore(true);
              fetchTimeline();
            }
          }}
          onEndReachedThreshold={0.5}
          ListFooterComponent={loadingMore ? <ActivityIndicator style={{ margin: 20 }} color={theme.colors.primary} /> : null}
          contentContainerStyle={styles.listContent}
        />
      )}

      {/* Post Menu Modal */}
      <Modal
        visible={postMenuVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setPostMenuVisible(false)}
      >
        <Pressable 
          style={styles.modalOverlay} 
          onPress={() => setPostMenuVisible(false)}
        >
          <View style={styles.menuContainer}>
            <TouchableOpacity style={styles.menuItem} onPress={executeRepost}>
              <Ionicons name="repeat" size={24} color={theme.colors.text} />
              <Text style={styles.menuText}>リポスト</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.menuItem} onPress={executeQuote}>
              <Ionicons name="create-outline" size={24} color={theme.colors.text} />
              <Text style={styles.menuText}>{t('chat.quote')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.menuItem} onPress={executeSave}>
              <Ionicons name="bookmark-outline" size={24} color={theme.colors.text} />
              <Text style={styles.menuText}>{t('timeline.save')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.menuItem} onPress={executeCopyLink}>
              <Ionicons name="copy-outline" size={24} color={theme.colors.text} />
              <Text style={styles.menuText}>{t('timeline.copyLink')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.menuItem} onPress={executeOpenInBrowser}>
              <Ionicons name="globe-outline" size={24} color={theme.colors.text} />
              <Text style={styles.menuText}>{t('timeline.openBrowser')}</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.menuItem, styles.cancelItem]} 
              onPress={() => setPostMenuVisible(false)}
            >
              <Text style={styles.cancelText}>キャンセル</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>

      {/* Quote Modal */}
      <Modal
        visible={quoteModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setQuoteModalVisible(false)}
      >
        <KeyboardAvoidingView 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.quoteModalContainer}
        >
          <View style={styles.quoteModalContent}>
            <View style={styles.quoteModalHeader}>
              <TouchableOpacity onPress={() => setQuoteModalVisible(false)}>
                <Text style={styles.quoteCancelText}>キャンセル</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.quoteSendButton, !quoteText.trim() && styles.quoteSendButtonDisabled]}
                onPress={handleSendQuote}
                disabled={!quoteText.trim() || isQuoteSending}
              >
                {isQuoteSending ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.quoteSendText}>投稿</Text>
                )}
              </TouchableOpacity>
            </View>
            
            {selectedPostForMenu && (
              <View style={styles.quoteTargetPreview}>
                <Image source={{ uri: selectedPostForMenu.post.author?.avatar }} style={styles.quoteTargetAvatar as any} />
                <View style={styles.quoteTargetInfo}>
                  <Text style={styles.quoteTargetAuthor}>@{selectedPostForMenu.post.author?.handle} を引用</Text>
                  <Text style={styles.quoteTargetText} numberOfLines={2}>{extractPlainText(selectedPostForMenu.post)}</Text>
                </View>
              </View>
            )}

            <TextInput
              style={styles.quoteInput}
              placeholder="コメントを追加"
              placeholderTextColor={theme.colors.textMuted}
              multiline
              autoFocus
              value={quoteText}
              onChangeText={setQuoteText}
              maxLength={300}
            />
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: theme.colors.overlay,
    justifyContent: 'flex-end',
  },
  menuContainer: {
    backgroundColor: theme.colors.card,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 40,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  menuText: {
    fontSize: 16,
    marginLeft: 12,
    color: theme.colors.text,
  },
  cancelItem: {
    justifyContent: 'center',
    borderBottomWidth: 0,
    marginTop: 8,
  },
  cancelText: {
    fontSize: 16,
    color: theme.colors.danger,
    fontWeight: 'bold',
  },
  quoteModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  quoteModalContent: {
    backgroundColor: theme.colors.card,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: '90%',
    padding: 15,
  },
  quoteModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  quoteCancelText: {
    fontSize: 16,
    color: theme.colors.text,
  },
  quoteSendButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 20,
  },
  quoteSendButtonDisabled: {
    opacity: 0.6,
  },
  quoteSendText: {
    color: theme.colors.primaryText,
    fontWeight: 'bold',
    fontSize: 16,
  },
  quoteTargetPreview: {
    flexDirection: 'row',
    marginBottom: 15,
    padding: 10,
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 12,
  },
  quoteTargetAvatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: theme.colors.cardSecondary,
  },
  quoteTargetInfo: {
    flex: 1,
    marginLeft: 10,
  },
  quoteTargetAuthor: {
    fontSize: 12,
    color: theme.colors.textMuted,
    marginBottom: 2,
  },
  quoteTargetText: {
    fontSize: 14,
    color: theme.colors.text,
  },
  quoteInput: {
    flex: 1,
    fontSize: 18,
    textAlignVertical: 'top',
    color: theme.colors.text,
  },
  header: {
    height: 60,
    backgroundColor: theme.colors.card,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  headerTitle: {
    color: theme.colors.text,
    fontSize: 20,
    fontWeight: 'bold',
  },
  headerIcons: {
    flexDirection: 'row',
  },
  headerIcon: {
    marginLeft: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
  },
  listContent: {
    paddingBottom: 20,
  },
  postItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  repostHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 8,
    paddingHorizontal: 16,
    backgroundColor: theme.colors.card,
  },
  repostText: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginLeft: 6,
  },
  postHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  postAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.cardSecondary,
    marginRight: 12,
  },
  postAuthorInfo: {
    flex: 1,
    justifyContent: 'center',
  },
  postAuthorNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  postAuthorName: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.text,
    marginRight: 6,
  },
  postAuthorHandle: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginTop: 1,
  },
  postContent: {
    marginLeft: 52,
  },
  postText: {
    fontSize: 15,
    color: theme.colors.text,
    lineHeight: 21,
    marginBottom: 8,
  },
  postImagesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
    marginBottom: 8,
    gap: 2,
  },
  postImage: {
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 12,
    overflow: 'hidden',
  },
  postImageSingle: {
    width: '100%',
    height: 280,
  },
  postImageDouble: {
    width: '49.5%',
    height: 200,
  },
  postImageTripleFirst: {
    width: '100%',
    height: 200,
  },
  postImageTripleOther: {
    width: '49.5%',
    height: 130,
  },
  postImageQuad: {
    width: '49.5%',
    height: 150,
  },
  postImageFull: {
    width: '100%',
    height: '100%',
  },
  postVideoWrapper: {
    marginTop: 8,
    marginBottom: 8,
    borderRadius: 12,
    overflow: 'hidden',
  },
  quotedPostVideoPreview: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginLeft: 10,
    overflow: 'hidden',
    backgroundColor: theme.colors.border,
  },
  quotedPostVideoPreviewInner: {
    borderRadius: 0,
  },
  quotedPost: {
    marginTop: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    backgroundColor: theme.colors.cardSecondary,
    overflow: 'hidden',
  },
  quotedPostImagePreview: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginLeft: 10,
    backgroundColor: theme.colors.border,
  },
  quotedPostHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  quotedPostAvatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
    marginRight: 6,
    backgroundColor: theme.colors.background,
  },
  quotedPostAuthor: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.colors.text,
    marginRight: 4,
    maxWidth: 120,
  },
  quotedPostHandle: {
    fontSize: 13,
    color: theme.colors.textMuted,
    flex: 1,
  },
  quotedPostText: {
    fontSize: 14,
    color: theme.colors.text,
    lineHeight: 19,
  },
  externalBox: {
    marginTop: 8,
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  externalThumb: {
    width: '100%',
    height: 180,
  },
  externalContent: {
    padding: 12,
  },
  externalTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: 4,
  },
  externalDescription: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginBottom: 4,
  },
  externalUri: {
    fontSize: 12,
    color: theme.colors.textMuted,
  },
  postFooter: {
    marginLeft: 52,
    marginTop: 8,
  },
  postInteractions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: 400,
  },
  postInteractionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginRight: 8,
  },
  postInteractionText: {
    marginLeft: 6,
    color: theme.colors.textMuted,
    fontSize: 13,
    fontWeight: '500',
  },
  postTime: {
    color: theme.colors.textMuted,
    fontSize: 13,
  },
});

export default Timeline;
